import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Badge,
    Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { BASE_URL } from '../api/config';
import { recupererDemandesBDC } from '../Services';
import InsuranceIndicator from './InsuranceIndicator';
import { adresseClient } from '../services/adresseClient';

interface FacturationDialogProps {
    open: boolean;
    onClose: () => void;
    demandeId: number;
    onSuccess?: () => void;
}

interface Demande {
    demande_id: number;
    libelle_visa: string;
    prix_consulat: number;
    prix_intervention: number;
    assurancevisa: string;
    nom_voyageur: string;
    prenom_voyageur: string;
    options?: string;
    validite_visa?: string;
}

const FacturationDialog: React.FC<FacturationDialogProps> = ({
    open,
    onClose,
    demandeId,
    onSuccess
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    // États pour l'adresse de facturation
    const [nom, setNom] = useState('');
    const [adresseLigne1, setAdresseLigne1] = useState('');
    const [adresseLigne2, setAdresseLigne2] = useState('');
    const [codePostal, setCodePostal] = useState('');
    const [ville, setVille] = useState('');
    const [pays, setPays] = useState('France');
    const [telephone, setTelephone] = useState('');
    const [email, setEmail] = useState('');

    // États pour les IDs
    const [idUtilisateur, setIdUtilisateur] = useState<number | null>(null);
    const [idAdresseFacturation, setIdAdresseFacturation] = useState<number | null>(null);
    const [idSage, setIdSage] = useState<number | null>(null);
    const [totalBDC, setTotalBDC] = useState<number | null>(null);
    const [urlFacture, setUrlFacture] = useState<string>('');

    // États pour la facturation
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [numFacture, setNumFacture] = useState('');
    const [etatPaiement, setEtatPaiement] = useState('');

    // États pour les demandes associées
    const [demandes, setDemandes] = useState<Demande[]>([]);
    const [bonDeCommandeId, setBonDeCommandeId] = useState<number | null>(null);

    // États pour les notifications
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // États pour les modales
    const [openVirementModal, setOpenVirementModal] = useState(false);
    const [openCBModal, setOpenCBModal] = useState(false);
    const [openPayeModal, setOpenPayeModal] = useState(false);
    const [openEnvoiFactureModal, setOpenEnvoiFactureModal] = useState(false);
    const [paypalLink, setPaypalLink] = useState('');

    // États pour les options dialog
    const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[] | null>(null);

    // États pour les emails
    const [virementEmail, setVirementEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });
    const [cbEmail, setCbEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });
    const [payeEmail, setPayeEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });
    const [envoiFactureEmail, setEnvoiFactureEmail] = useState({
        subject: '',
        content: '',
        sendEmail: true
    });

    // Charger les informations de la demande
    useEffect(() => {
        if (open && demandeId) {
            fetchDemandeInfo();
        }
    }, [open, demandeId]);

    const fetchDemandeInfo = async () => {
        try {
            // Récupérer les infos de la demande
            const response = await axios.get(`${BASE_URL}/demandes/${demandeId}`);
            const demande = response.data;

            // Récupérer les infos du BDC associé s'il existe
            const bdcResponse = await axios.get(`${BASE_URL}/demandes/${demandeId}/bdc`);
            if (bdcResponse.data && bdcResponse.data.id) {
                const bdc = bdcResponse.data;
                setBonDeCommandeId(bdc.id);

                // Remplir les champs d'adresse
                setNom(bdc.nom || '');
                setAdresseLigne1(bdc.adresse_ligne1 || '');
                setAdresseLigne2(bdc.adresse_ligne2 || '');
                setCodePostal(bdc.code_postal || '');
                setVille(bdc.ville || '');
                setPays(bdc.pays || 'France');
                setTelephone(bdc.telephone || '');
                // Adresse affichée = celle où partent les mails (sans le préfixe « externe. » des sites Inde)
                setEmail(adresseClient(bdc.email, demande.source));
                setEtatPaiement(bdc.etat || '');
                setNumFacture(bdc.numfacture || '');

                // Remplir les IDs
                setIdUtilisateur(bdc.id_utilisateur ?? null);
                setIdAdresseFacturation(bdc.id_adresse_facturation ?? null);
                setIdSage(bdc.id_sage ?? null);
                setTotalBDC(bdc.total ?? null);
                setUrlFacture(bdc.urlfacture || '');

                // Récupérer les demandes associées au BDC
                const demandesBDC = await recupererDemandesBDC(bdc.id);
                setDemandes(demandesBDC);
            } else {
                // Pas de BDC, charger depuis les infos de la demande
                setEmail(adresseClient(demande.email, demande.source));
                setEtatPaiement(demande.etat_paiement || '');

                // Créer une "demande" pour l'affichage
                setDemandes([{
                    demande_id: demande.id,
                    libelle_visa: demande.libelle_visa,
                    prix_consulat: demande.prix_consulat,
                    prix_intervention: demande.prix_intervention,
                    assurancevisa: demande.assurancevisa,
                    nom_voyageur: demande.nom,
                    prenom_voyageur: demande.prenom,
                    options: demande.options,
                    validite_visa: demande.validite_visa
                }]);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des informations:', error);
        }
    };

    const handleMouseOver = (event: React.MouseEvent) => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(event.currentTarget);
        selection?.removeAllRanges();
        selection?.addRange(range);
    };

    const handleUploadClick = () => {
        if (!numFacture.trim()) {
            alert('Veuillez saisir un numéro de facture avant de sélectionner un fichier.');
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setSelectedFile(files[0]);
        }
        event.target.value = '';
    };

    const handleCloseSnackbar = () => {
        setUploadSuccess(false);
        setUploadError('');
    };

    const uploadFacture = async () => {
        if (!selectedFile) return;
        if (!bonDeCommandeId) {
            alert('Aucun bon de commande associé. Veuillez d\'abord créer un BDC.');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('bonDeCommandeId', bonDeCommandeId.toString());
        formData.append('numfacture', numFacture);

        try {
            const response = await axios.post(
                `${BASE_URL}/uploadFacture`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setUploadSuccess(true);
            setUploadError('');
            console.log('Fichier uploadé avec succès:', response.data);
        } catch (error) {
            setUploadSuccess(false);
            setUploadError("Erreur lors de l'upload du fichier.");
            console.error("Erreur lors de l'upload du fichier:", error);
            throw error;
        }
    };

    const handleVirement = () => {
        if (!selectedFile) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }

        const defaultSubject = "Votre facture est prête";
        const defaultContent = `Bonjour ${nom || ''},

Votre commande est bien enregistrée dans notre système.
Vous trouverez ci-dessous le lien vous permettant d'accéder à votre facture (disponible aussi dans votre espace client).

[Lien vers la facture sera ajouté automatiquement]

Les frais de visa étant avancés pour votre compte, nous vous remercions de bien vouloir procéder au paiement sans délai.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setVirementEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenVirementModal(true);
    };

    const confirmVirement = async () => {
        if (!bonDeCommandeId) return;

        try {
            await uploadFacture();
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommandeId}/virement`,
                {
                    subjectMail: virementEmail.subject,
                    mailContent: virementEmail.content,
                    sendEmail: virementEmail.sendEmail
                }
            );
            setOpenVirementModal(false);
            alert(virementEmail.sendEmail
                ? "BDC mis en état 'virement'. Mail envoyé au client pour virement."
                : "BDC mis en état 'virement' (sans envoi d'email).");
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Erreur lors de la mise à jour du BDC en 'virement':", error);
            alert("Erreur lors de la mise à jour du BDC.");
        }
    };

    const handleOpenCBModal = () => {
        if (!selectedFile) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }
        setPaypalLink('');

        const defaultSubject = "Votre facture et votre lien de paiement";
        const defaultContent = `Bonjour ${nom || ''},

Votre commande est bien enregistrée dans notre système.
Afin que nos équipes la prennent en charge, vous trouverez ci-dessous le lien de paiement pour régler la facture.

[Le bouton de paiement sera ajouté automatiquement]

Le lien vous permettant d'accéder à votre facture (disponible aussi dans votre espace client) :
[Lien vers la facture sera ajouté automatiquement]

Dès réception de votre paiement, nos équipes prendront en charge vos demandes.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setCbEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenCBModal(true);
    };

    const handleEnvoyerLienCB = async () => {
        if (!selectedFile || !bonDeCommandeId) {
            alert("Veuillez d'abord sélectionner et uploader la facture PDF avant d'effectuer cette action.");
            return;
        }

        try {
            await uploadFacture();
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommandeId}/envoyer-lien-cb`,
                {
                    paypalLink,
                    subjectMail: cbEmail.subject,
                    mailContent: cbEmail.content,
                    sendEmail: cbEmail.sendEmail
                }
            );
            alert(cbEmail.sendEmail
                ? "Lien CB envoyé avec succès !"
                : "BDC mis en 'attente paiement' (sans envoi d'email).");
            setOpenCBModal(false);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Erreur lors de l'envoi du lien CB:", error);
            alert("Erreur lors de l'envoi du lien CB.");
        }
    };

    const handlePasserEnPaye = () => {
        const defaultSubject = "Nous avons bien reçu votre paiement.";
        const defaultContent = `Bonjour ${nom || ''},

Nous accusons bonne réception de votre règlement et vous en remercions.

Votre dossier est pris en charge par nos équipes.
Des mails de suivis de dossier vous seront envoyés à chaque étape de votre commande.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setPayeEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenPayeModal(true);
    };

    const confirmPasserEnPaye = async () => {
        if (!bonDeCommandeId) return;

        try {
            await axios.put(
                `${BASE_URL}/bdc/${bonDeCommandeId}/passer-paye`,
                {
                    subjectMail: payeEmail.subject,
                    mailContent: payeEmail.content,
                    sendEmail: payeEmail.sendEmail
                }
            );
            setOpenPayeModal(false);
            alert(payeEmail.sendEmail
                ? "BDC passé en payé avec succès !"
                : "BDC passé en payé (sans envoi d'email).");
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Erreur lors de la mise à jour du BDC en payé:", error);
            alert("Erreur lors de la mise à jour du BDC.");
        }
    };

    // Fonction pour ouvrir le modal d'envoi de facture
    const handleOpenEnvoiFacture = () => {
        // Vérifier qu'on a soit une URL de facture existante, soit un fichier sélectionné à uploader
        if (!urlFacture && !selectedFile) {
            alert("Veuillez d'abord sélectionner une facture PDF à uploader.");
            return;
        }

        if (!urlFacture && selectedFile && !numFacture.trim()) {
            alert("Veuillez saisir un numéro de facture avant d'envoyer.");
            return;
        }

        const defaultSubject = "Votre facture Visa Monde";
        const defaultContent = `Bonjour ${nom || ''},

Veuillez trouver ci-dessous le lien vers votre facture :

[Lien vers la facture sera ajouté automatiquement]

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

        setEnvoiFactureEmail({
            subject: defaultSubject,
            content: defaultContent,
            sendEmail: true
        });
        setOpenEnvoiFactureModal(true);
    };

    // Fonction pour confirmer l'envoi de la facture
    const confirmEnvoiFacture = async () => {
        if (!bonDeCommandeId) return;

        try {
            // Si on a un fichier sélectionné mais pas encore d'URL, on uploade d'abord
            if (selectedFile && !urlFacture) {
                await uploadFacture();
            }

            await axios.post(
                `${BASE_URL}/bdc/${bonDeCommandeId}/envoyer-facture`,
                {
                    subjectMail: envoiFactureEmail.subject,
                    mailContent: envoiFactureEmail.content,
                    sendEmail: envoiFactureEmail.sendEmail
                }
            );
            setOpenEnvoiFactureModal(false);
            alert(envoiFactureEmail.sendEmail
                ? "Facture envoyée avec succès !"
                : "Action effectuée (sans envoi d'email).");
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Erreur lors de l'envoi de la facture:", error);
            alert("Erreur lors de l'envoi de la facture.");
        }
    };

    const handleOpenOptionsDialog = (optionsString: string | undefined) => {
        if (!optionsString) {
            setSelectedOptions([]);
        } else {
            const splittedOptions = optionsString.split(';').filter(Boolean);
            setSelectedOptions(splittedOptions);
        }
        setOptionsDialogOpen(true);
    };

    const handleCloseOptionsDialog = () => {
        setOptionsDialogOpen(false);
        setSelectedOptions(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '900px'
                    }
                }}
            >
                <DialogTitle>
                    Facturation - Demande #{demandeId}
                    {bonDeCommandeId && (
                        <Typography component="span" sx={{ ml: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            (Bon de commande #{bonDeCommandeId})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {/* Cards pour les IDs */}
                        {/* Cards IDs commentées
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ backgroundColor: '#e3f2fd', minHeight: 100 }}>
                                    <CardContent>
                                        <Typography color="primary" variant="subtitle2" gutterBottom>
                                            ID Utilisateur
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {idUtilisateur || 'N/A'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Card sx={{ backgroundColor: '#f3e5f5', minHeight: 100 }}>
                                    <CardContent>
                                        <Typography color="secondary" variant="subtitle2" gutterBottom>
                                            ID Adresse Facturation
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {idAdresseFacturation || 'Non défini'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Card sx={{ backgroundColor: idSage ? '#e8f5e9' : '#ffebee', minHeight: 100 }}>
                                    <CardContent>
                                        <Typography
                                            sx={{ color: idSage ? '#2e7d32' : '#c62828' }}
                                            variant="subtitle2"
                                            gutterBottom
                                        >
                                            ID Sage (Ancienne base Client)
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {idSage || 'Pas de client Sage'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        */}

                        {/* Adresse de facturation en glissé-déposé */}
                        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" gutterBottom>
                                Adresse de facturation
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography>
                                        Nom : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{nom}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography>
                                        Adresse : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{adresseLigne1}</span>
                                    </Typography>
                                </Grid>
                                {adresseLigne2 && (
                                    <Grid item xs={12}>
                                        <Typography>
                                            Adresse ligne 2 : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{adresseLigne2}</span>
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography>
                                        Code Postal : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{codePostal}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography>
                                        Ville : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{ville}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>
                                        Pays : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{pays}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>
                                        Email : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{email}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>
                                        Téléphone : <span onMouseOver={handleMouseOver} style={{ cursor: 'pointer', userSelect: 'all' }}>{telephone}</span>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Détails des demandes */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Détails des demandes
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Dossier</TableCell>
                                            <TableCell>Libellé Visa</TableCell>
                                            <TableCell>Validité</TableCell>
                                            <TableCell>Prix Consulat</TableCell>
                                            <TableCell>Prix Intervention</TableCell>
                                            <TableCell>Options</TableCell>
                                            <TableCell>Assurance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {demandes.map((demande) => {
                                            const isEvisa = demande.libelle_visa.toLowerCase().includes('evisa');
                                            const optionsArray = demande.options
                                                ? demande.options.split(';').filter(Boolean)
                                                : [];

                                            return (
                                                <TableRow key={demande.demande_id}>
                                                    <TableCell>
                                                        <span
                                                            onMouseOver={handleMouseOver}
                                                            style={{
                                                                cursor: 'pointer',
                                                                userSelect: 'all',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {demande.demande_id} - {demande.nom_voyageur} {demande.prenom_voyageur}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{demande.libelle_visa}</TableCell>
                                                    <TableCell>
                                                        {demande.validite_visa && demande.validite_visa.length > 20 ? (
                                                            <Tooltip title={demande.validite_visa} arrow>
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    maxWidth: '150px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {demande.validite_visa}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span>{demande.validite_visa || '-'}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{demande.prix_consulat}€</TableCell>
                                                    <TableCell>{demande.prix_intervention}€</TableCell>
                                                    <TableCell>
                                                        {!isEvisa && (
                                                            <IconButton size="small" onClick={() => handleOpenOptionsDialog(demande.options)}>
                                                                <Badge
                                                                    badgeContent={optionsArray.length}
                                                                    color={optionsArray.length > 0 ? 'primary' : 'default'}
                                                                >
                                                                    <InfoIcon fontSize="small" />
                                                                </Badge>
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <InsuranceIndicator assurancevisa={demande.assurancevisa} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* État du paiement et Total */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        État du paiement
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                        {etatPaiement || 'Non défini'}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#e8f5e9', height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Montant total BDC
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                        {totalBDC !== null ? `${totalBDC.toFixed(2)} €` : 'N/A'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Numéro de facture */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" gutterBottom>
                                Numéro de facture :
                            </Typography>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={numFacture}
                                onChange={(e) => setNumFacture(e.target.value)}
                                placeholder="Ex: 2023-001"
                            />
                        </Box>

                        {/* Sélection de fichier */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" gutterBottom>
                                Sélectionner la facture PDF (après avoir saisi le numéro de facture) :
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUploadClick}
                            >
                                Choisir le Fichier
                            </Button>
                            <input
                                type="file"
                                accept=".pdf"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            {selectedFile && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Fichier sélectionné : {selectedFile.name}
                                </Typography>
                            )}
                        </Box>

                        {/* Boutons d'action */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'center',
                                flexWrap: 'wrap'
                            }}
                        >
                            <Button variant="contained" color="primary" onClick={handleOpenEnvoiFacture}>
                                Envoyer facture
                            </Button>
                            {/* Boutons commentés
                            <Button variant="contained" color="info" onClick={handleVirement}>
                                Virement
                            </Button>
                            <Button variant="contained" color="secondary" onClick={handleOpenCBModal}>
                                Envoi lien CB
                            </Button>
                            <Button variant="contained" color="success" onClick={handlePasserEnPaye}>
                                Passer BDC en payé
                            </Button>
                            */}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Fermer</Button>
                </DialogActions>
            </Dialog>

            {/* Notifications */}
            {uploadSuccess && (
                <Snackbar open={uploadSuccess} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        Fichier uploadé avec succès !
                    </Alert>
                </Snackbar>
            )}
            {uploadError && (
                <Snackbar
                    open={Boolean(uploadError)}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {uploadError}
                    </Alert>
                </Snackbar>
            )}

            {/* Modal Virement */}
            <Dialog
                open={openVirementModal}
                onClose={() => setOpenVirementModal(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Prévisualisation de l'email - Virement</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{email}</strong>
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={virementEmail.sendEmail}
                                onChange={(e) => setVirementEmail({...virementEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={virementEmail.subject}
                        onChange={(e) => setVirementEmail({...virementEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!virementEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={12}
                        value={virementEmail.content}
                        onChange={(e) => setVirementEmail({...virementEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!virementEmail.sendEmail}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenVirementModal(false)}>Annuler</Button>
                    <Button onClick={confirmVirement} variant="contained" color="primary">
                        {virementEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal CB */}
            <Dialog
                open={openCBModal}
                onClose={() => setOpenCBModal(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Prévisualisation de l'email - Lien CB</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{email}</strong>
                        </Typography>
                    </Box>

                    <TextField
                        margin="normal"
                        label="Lien PayPal/CB"
                        variant="outlined"
                        fullWidth
                        value={paypalLink}
                        onChange={(e) => setPaypalLink(e.target.value)}
                        helperText="Ce lien sera intégré dans l'email sous forme de bouton"
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={cbEmail.sendEmail}
                                onChange={(e) => setCbEmail({...cbEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={cbEmail.subject}
                        onChange={(e) => setCbEmail({...cbEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!cbEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={10}
                        value={cbEmail.content}
                        onChange={(e) => setCbEmail({...cbEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!cbEmail.sendEmail}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCBModal(false)}>Annuler</Button>
                    <Button onClick={handleEnvoyerLienCB} variant="contained" color="primary">
                        {cbEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Payé */}
            <Dialog
                open={openPayeModal}
                onClose={() => setOpenPayeModal(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Prévisualisation de l'email - Confirmation de paiement</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{email}</strong>
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={payeEmail.sendEmail}
                                onChange={(e) => setPayeEmail({...payeEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={payeEmail.subject}
                        onChange={(e) => setPayeEmail({...payeEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!payeEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={12}
                        value={payeEmail.content}
                        onChange={(e) => setPayeEmail({...payeEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!payeEmail.sendEmail}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPayeModal(false)}>Annuler</Button>
                    <Button onClick={confirmPasserEnPaye} variant="contained" color="primary">
                        {payeEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Envoi Facture */}
            <Dialog
                open={openEnvoiFactureModal}
                onClose={() => setOpenEnvoiFactureModal(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Prévisualisation de l'email - Envoi de facture</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Destinataire : <strong>{email}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Lien de la facture : <a href={urlFacture} target="_blank" rel="noopener noreferrer">{urlFacture}</a>
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={envoiFactureEmail.sendEmail}
                                onChange={(e) => setEnvoiFactureEmail({...envoiFactureEmail, sendEmail: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Envoyer l'email au client"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        label="Objet de l'email"
                        variant="outlined"
                        fullWidth
                        value={envoiFactureEmail.subject}
                        onChange={(e) => setEnvoiFactureEmail({...envoiFactureEmail, subject: e.target.value})}
                        margin="normal"
                        disabled={!envoiFactureEmail.sendEmail}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Contenu de l'email"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={12}
                        value={envoiFactureEmail.content}
                        onChange={(e) => setEnvoiFactureEmail({...envoiFactureEmail, content: e.target.value})}
                        margin="normal"
                        disabled={!envoiFactureEmail.sendEmail}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEnvoiFactureModal(false)}>Annuler</Button>
                    <Button onClick={confirmEnvoiFacture} variant="contained" color="primary">
                        {envoiFactureEmail.sendEmail ? 'Confirmer et envoyer' : 'Confirmer sans envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Options */}
            <Dialog open={optionsDialogOpen} onClose={handleCloseOptionsDialog}>
                <DialogTitle>Options</DialogTitle>
                <DialogContent>
                    {selectedOptions && selectedOptions.length > 0 ? (
                        <ul>
                            {selectedOptions.map((opt, index) => (
                                <li key={index}>{opt}</li>
                            ))}
                        </ul>
                    ) : (
                        <p>Aucune option</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseOptionsDialog}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FacturationDialog;
